import type { ComponentType, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { DitherField } from './dither-field.tsx';

// Empty state que ensina a próxima ação: ícone sutil, título curto, explicação e ação
// opcional. Por padrão numa moldura tracejada; `bare` remove a moldura para quando o empty
// já vive dentro de um `Card` (evita card dentro de card).
//
// `dither` é o vazio de PRIMEIRA VISTA de uma tela (conta nova): um bloco de tinta com o
// pontilhado da marca à direita e o texto alinhado à esquerda, pra tela vazia parecer um
// começo, não um defeito. Só nas telas principais; vazio de filtro e de painel pequeno
// seguem no desenho neutro.

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  bare = false,
  dither = false,
  className,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  bare?: boolean;
  dither?: boolean;
  className?: string;
}) {
  if (dither) {
    return (
      <div
        className={cn(
          'on-ink dither-stage relative grid min-h-44 content-center justify-items-start gap-2 overflow-hidden rounded-2xl bg-stage px-6 py-8 text-foreground',
          bare ? '' : 'shadow-(--shadow-card)',
          className,
        )}
      >
        <DitherField still overlay className="absolute inset-0 size-full" />
        <p className="relative text-16 font-semibold">{title}</p>
        {description !== undefined && (
          <p className="relative max-w-[38ch] text-14 text-muted-foreground">{description}</p>
        )}
        {action !== undefined && <div className="relative mt-2">{action}</div>}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'grid justify-items-center gap-2 px-6 py-10 text-center',
        bare ? '' : 'rounded-2xl border border-border border-dashed',
        className,
      )}
    >
      {Icon !== undefined && (
        // Mesmo tile da marca do `ErrorState` (36px, raio 12): vazio, carregando e erro
        // são a mesma família.
        <span className="grid size-9 place-items-center rounded-lg bg-secondary text-muted-foreground">
          <Icon className="size-4.5" />
        </span>
      )}
      <p className="mt-1 text-14 font-semibold">{title}</p>
      {description !== undefined && (
        <p className="max-w-sm text-14 text-muted-foreground text-balance">{description}</p>
      )}
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}
